import { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { supabase } from '@/lib/supabase';
import {
  Delete,
  Edit,
  X,
  Search,
  Users,
  Building2,
  MapPin,
  Filter,
} from 'lucide-react';
import {
  useAdminUserFilters,
  UserFilterType,
} from '@/lib/hooks/useAdminUserFilters';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useOrganizations,
  extractProfiles,
  countUsers,
} from '@/lib/hooks/use-organizations';
import { OrganizationOverview, Team, Profile } from '@/types/custom';
import { formatDate, getAvatarUrl, getIdenticon } from '@/lib/utils';
import { useAdminPreview } from '@/lib/admin-preview-context';
import { useToast } from '@/components/ui/use-toast';

interface DummyTeam {
  id: string;
  name: string;
  organization: string;
  userCount: number;
  location: string;
  manager: string;
  managerEmail: string;
  managerAvatar: string;
}

interface Organization {
  name: string;
  employees: number;
  contacts: {
    name: string;
    email: string;
    phone: string;
    avatar: string;
  }[];
  logo?: string;
}

const DUMMY_TEAMS: DummyTeam[] = [
  {
    id: '1',
    name: 'Kempenhof',
    organization: 'Valkenhof',
    userCount: 42,
    location: 'Eindhoven',
    manager: 'Jessica Rodriguez',
    managerEmail: 'jessica.rodriguez@example.com',
    managerAvatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Jessica',
  },
  {
    id: '2',
    name: 'Wissehaege',
    organization: 'Valkenhof',
    userCount: 38,
    location: 'Eindhoven',
    manager: 'Steven Mcguire',
    managerEmail: 'steven.mcguire@example.com',
    managerAvatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Steven',
  },
];

const DUMMY_ORGANIZATIONS = [
  {
    name: 'Valkenhof',
    employees: 125,
    contacts: [
      {
        name: 'Jessica Rodriguez',
        email: 'jessica.rodriguez@example.com',
        phone: '+31 5 55555555',
        avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Jessica',
      },
      {
        name: 'Thomas Wilson',
        email: 'thomas.wilson@example.com',
        phone: '+31 5 55555555',
        avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Thomas',
      },
    ],
    logo: '/images/valkenhof-logo.png',
  },
  {
    name: 'John Smith',
    employees: 2258,
    contacts: [
      {
        name: 'Steven Mcguire',
        email: 'steven.mcguire@mail.com',
        phone: '+31 6 23456789',
        avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Steven',
      },
      {
        name: 'Emma Johnson',
        email: 'emma.johnson@mail.com',
        phone: '+31 6 34567890',
        avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Emma',
      },
      {
        name: 'Michael Brown',
        email: 'michael.brown@mail.com',
        phone: '+31 6 45678901',
        avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Michael',
      },
    ],
    logo: 'https://images.unsplash.com/photo-1572021335469-31706a17aaef?w=100&q=80',
  },
];

// TOOD: split into 3 components (one for each tab)
export default function AdminUsers() {
  const { isPreviewMode } = useAdminPreview();
  const [activeTab, setActiveTab] = useState<
    'gebruikers' | 'organisaties' | 'teams'
  >('gebruikers');

  const [organizations, looseProfiles, organizationsLoading] =
    useOrganizations();

  const [orgSearchTerm, setOrgSearchTerm] = useState('');

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  const [filteredTeams, setFilteredTeams] = useState<Team[]>([]);
  const [teamSearchTerm, setTeamSearchTerm] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const [userToEdit, setUserToEdit] = useState(null);
  const [teamToEdit, setTeamToEdit] = useState(null);
  const [orgToEdit, setOrgToEdit] = useState(null);

  const [teamDeleteDialogOpen, setTeamDeleteDialogOpen] = useState(false);
  const [teamToDelete, setTeamToDelete] = useState(null);

  // HnH creation dialogs
  const { toast } = useToast();
  const [hnhSamenDialogOpen, setHnhSamenDialogOpen] = useState(false);
  const [hnhUserDialogOpen, setHnhUserDialogOpen] = useState(false);
  const [hnhSaving, setHnhSaving] = useState(false);
  const [hnhInviteCode, setHnhInviteCode] = useState('');
  const [hnhSelectedTeamId, setHnhSelectedTeamId] = useState<string>('');

  // Fetch hoofdhartuser data for preview mode
  const [hnhUserIds, setHnhUserIds] = useState<Set<string>>(new Set());
  const [samenGroups, setSamenGroups] = useState<{ id: string; name: string; member_count: number }[]>([]);
  const [userSamenMap, setUserSamenMap] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!isPreviewMode) {
      setHnhUserIds(new Set());
      setSamenGroups([]);
      return;
    }
    const fetchHnhData = async () => {
      // Fetch hoofdhartuser user IDs
      const { data: roleData } = await supabase
        .from('profile_role')
        .select('user_id')
        .eq('role', 'hoofdhartuser');
      if (roleData) {
        setHnhUserIds(new Set(roleData.map((r) => r.user_id)));
      }

      // Fetch samen groups with member counts and build user→samen mapping
      const { data: samenData } = await supabase
        .from('samen')
        .select('id, name, samen_user_link(user_id)');
      if (samenData) {
        setSamenGroups(samenData.map((s: any) => ({
          id: s.id,
          name: s.name,
          member_count: s.samen_user_link?.length ?? 0,
        })));

        const mapping: Record<string, string> = {};
        for (const s of samenData) {
          for (const link of (s as any).samen_user_link ?? []) {
            mapping[link.user_id] = s.name;
          }
        }
        setUserSamenMap(mapping);
      }
    };
    fetchHnhData();
  }, [isPreviewMode]);

  // Switch away from organisaties tab when entering preview mode
  useEffect(() => {
    if (isPreviewMode && activeTab === 'organisaties') {
      setActiveTab('teams');
    }
  }, [isPreviewMode]);

  // Extract all profiles from organizations, flatten them, and remove duplicates
  const allUsers: Profile[] = useMemo(() => {
    if (!organizations) return [];
    return [...extractProfiles(organizations), ...looseProfiles];
  }, [organizations, looseProfiles]);

  // In preview mode, filter to only hoofdhartuser profiles
  const users: Profile[] = useMemo(() => {
    if (!isPreviewMode || hnhUserIds.size === 0) return allUsers;
    return allUsers.filter((u) => hnhUserIds.has(u.id));
  }, [allUsers, isPreviewMode, hnhUserIds]);

  type TeamWithUsers = Team & { users: Profile[] };

  const allTeams: TeamWithUsers[] = useMemo(() => {
    if (!organizations) return [];
    return organizations.map((org) => org.teams).flat();
  }, [organizations]);

  const teams: TeamWithUsers[] = useMemo(() => {
    if (!isPreviewMode) return allTeams;
    return []; // In preview mode, we use samenGroups instead
  }, [allTeams, isPreviewMode]);

  // Use the custom hook for user filtering
  const {
    filters,
    filteredUsers,
    setFilteredUsers,
    updateSearchTerm,
    updateFilterType,
  } = useAdminUserFilters(users as any[]);

  // State to track filtered users by team/location
  const [teamFilterActive, setTeamFilterActive] = useState(false);
  const [activeTeamFilter, setActiveTeamFilter] = useState(null);
  const [expandedTeam, setExpandedTeam] = useState(null);

  const [filteredOrganizations, setFilteredOrganizations] = useState<
    OrganizationOverview[]
  >([]);

  // Track expanded state for each organization
  const [expandedOrgs, setExpandedOrgs] = useState({});
  const [singleOrgView, setSingleOrgView] =
    useState<OrganizationOverview | null>(null);

  const userIdToTeam = useMemo(
    () =>
      (organizations || []).reduce(
        (acc, org) => {
          org.teams.forEach((team) => {
            team.users.forEach((user) => {
              acc[user.id] = team;
            });
          });
          return acc;
        },
        {} as Record<string, Team>,
      ),
    [organizations],
  );

  const teamIdToOrg = useMemo(
    () =>
      (organizations || []).reduce(
        (acc, org) => {
          org.teams.forEach((team) => {
            acc[team.id] = org;
          });
          return acc;
        },
        {} as Record<string, OrganizationOverview>,
      ),
    [organizations],
  );

  // Function to toggle expanded state
  const toggleOrgExpanded = (orgName) => {
    setExpandedOrgs((prev) => ({
      ...prev,
      [orgName]: !prev[orgName],
    }));
  };

  // Function to view a single organization
  const viewSingleOrg = (org: OrganizationOverview) => {
    setSingleOrgView(org);
  };

  // Function to go back to all organizations
  const backToAllOrgs = () => {
    setSingleOrgView(null);
    // Reset any active filters
    clearTeamFilter();
    setExpandedTeam(null);
  };

  // Function to filter users by team/location
  /*
  const filterUsersByTeam = (teamName, orgName) => {
    setActiveTab('gebruikers');
    setTeamFilterActive(true);
    setActiveTeamFilter({ team: teamName, organization: orgName });

    // Find the team data to display
    const teamData = teams.find(
      (team) => team.name === teamName && team.organization === orgName,
    );
    if (teamData) {
      setExpandedTeam(teamData);
    }

    // Filter users based on team and organization
    if (users && users.length > 0) {
      const filtered = users.filter(
        (user) =>
          (user.team === teamName ||
            (user.team && user.team.includes(teamName))) &&
          (user.organisatie === orgName ||
            (user.organisatie && user.organisatie.includes(orgName))),
      );
      setFilteredUsers(filtered);
    }
  };
  */

  // Function to clear team filter
  const clearTeamFilter = () => {
    setTeamFilterActive(false);
    setActiveTeamFilter(null);
    // Reset to the default filtering from the hook
    updateSearchTerm('');
    updateFilterType('all');
    setExpandedTeam(null);
  };

  // filter orgs
  useEffect(() => {
    if (orgSearchTerm.trim() === '') {
      return setFilteredOrganizations(organizations);
    }

    const lowercaseValue = orgSearchTerm.toLowerCase();
    // TODO: add contact details to orgs? or just a notes field (let's avoid scope creep)
    const filtered = organizations.filter((org) =>
      org.name.toLowerCase().includes(lowercaseValue),
    );
    // || org.contacts.some(
    //   (contact) =>
    //     contact.name.toLowerCase().includes(lowercaseValue) ||
    //     contact.email.toLowerCase().includes(lowercaseValue),
    // ),
    // );
    setFilteredOrganizations(filtered);
  }, [orgSearchTerm, organizations]);

  // filter teams
  useEffect(() => {
    if (teamSearchTerm.trim() === '') {
      setFilteredTeams(teams);
    } else {
      const lowercaseValue = teamSearchTerm.toLowerCase();
      const filtered = teams.filter(
        (team) => team.name.toLowerCase().includes(lowercaseValue),
        // team.organization.toLowerCase().includes(lowercaseValue) ||
        // team.location.toLowerCase().includes(lowercaseValue) ||
        // team.manager.toLowerCase().includes(lowercaseValue) ||
        // team.managerEmail.toLowerCase().includes(lowercaseValue),
      );
      setFilteredTeams(filtered);
    }
  }, [teamSearchTerm, teams]);

  // This effect handles team filtering separately from the general user filtering
  /*
  useEffect(() => {
    if (teamFilterActive && activeTeamFilter) {
      // Filter users based on team and organization
      const teamFilteredUsers = users.filter(
        (user) =>
          (user.team === activeTeamFilter.team ||
            (user.team && user.team.includes(activeTeamFilter.team))) &&
          (user.organisatie === activeTeamFilter.organization ||
            (user.organisatie &&
              user.organisatie.includes(activeTeamFilter.organization))),
      );
      // Override the filteredUsers when team filter is active
      // Using the hook's function instead of direct state setter
      updateSearchTerm(''); // Reset search term to ensure filter takes effect
    }
  }, [teamFilterActive, activeTeamFilter, users, updateSearchTerm]);
  */

  // TODO: make some way to delete the auth.user entry as well (perhaps via cascade)
  //  https://www.reddit.com/r/Supabase/comments/1b43n1f/comment/kswpktx/
  async function deleteUser(id: string) {
    try {
      const { error } = await supabase.from('profiles').delete().eq('id', id);
      if (error) throw error;

      // TODO: refresh organizations state
    } catch (error) {
      console.error('Error deleting user:', error);
    }
  }

  function deleteTeam(id) {
    // TODO: actually delete team
    // but warn first it will delete all the team's data (but not users)
  }

  // Render the team filter header
  const renderTeamFilterHeader = () => {
    return (
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              clearTeamFilter();
              // Find the organization and view it
              const org = organizations.find(
                (o) => o.name === activeTeamFilter.organization,
              );
              if (org) {
                setActiveTab('organisaties');
                viewSingleOrg(org);
              }
            }}
            className="text-sm flex items-center gap-1 text-green-600 hover:text-green-700"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M19 12H5" />
              <path d="M12 19l-7-7 7-7" />
            </svg>
            <span>Terug naar Organisatie</span>
          </Button>
          <div className="flex items-center gap-2">
            <span className="font-medium text-lg">{expandedTeam?.name}</span>
            <span className="text-gray-500">
              ({expandedTeam?.organization})
            </span>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={clearTeamFilter}
          className="text-xs flex items-center gap-1"
        >
          <X size={14} />
          Filter wissen
        </Button>
      </div>
    );
  };

  // Render the users table
  const renderUsersTable = (users: Profile[]) => {
    const looseIds = looseProfiles.map((p) => p.id);
    return (
      <div className="border rounded-md overflow-hidden">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-gray-50 border-b">
              <th className="text-left p-2 pl-3"></th> {/* (avatar) */}
              <th className="text-left p-2 pl-3">Email</th>
              <th className="text-left p-2">Naam</th>
              <th className="text-left p-2">Telefoonnummer</th>
              {!expandedTeam && (
                isPreviewMode ? (
                  <>
                    <th className="text-left p-2">Samen</th>
                    <th className="text-left p-2">Aangemaakt op</th>
                  </>
                ) : (
                  <>
                    <th className="text-left p-2">Organisatie</th>
                    <th className="text-left p-2">Locatie / Team</th>
                    <th className="text-left p-2">Aangemaakt op</th>
                  </>
                )
              )}
              <th className="text-left p-2 w-10">Acties</th>
            </tr>
          </thead>
          <tbody>
            {organizationsLoading || !organizations ? (
              <tr>
                <td colSpan={expandedTeam ? 5 : 8} className="p-2 text-center">
                  Loading gebruikers...
                </td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan={expandedTeam ? 5 : 8} className="p-2 text-center">
                  {filteredUsers.length === 0
                    ? 'Geen gebruikers gevonden'
                    : 'Geen resultaten gevonden voor uw zoekopdracht'}
                </td>
              </tr>
            ) : (
              users.map((user) => {
                const userTeam = userIdToTeam[user.id];
                const userOrg = userTeam ? teamIdToOrg[userTeam.id] : undefined;
                return (
                  <tr key={user.id} className="border-b hover:bg-gray-50">
                    <td className="p-2 pl-3">
                      <div className="w-8 h-8 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center">
                        {user.avatar_url ? (
                          <img
                            src={user.avatar_url}
                            alt={user.naam || 'User'}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <img
                            src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${user.naam || user.email || user.id}`}
                            alt={user.naam || 'User'}
                            className="w-full h-full object-cover"
                          />
                        )}
                      </div>
                    </td>
                    <td className="p-2 pl-3">
                      <a
                        href={`mailto:${user.email}`}
                        className="text-blue-500 hover:underline"
                      >
                        {user.email}
                      </a>
                    </td>
                    <td className="p-2">{user.naam}</td>
                    <td className="p-2">
                      {user.telefoonnummer ? (
                        <a
                          href={`tel:${user.telefoonnummer}`}
                          className="text-blue-500 hover:underline"
                        >
                          {user.telefoonnummer}
                        </a>
                      ) : (
                        '-'
                      )}
                    </td>
                    {!expandedTeam && (
                      isPreviewMode ? (
                        <>
                          <td className="p-2">{userSamenMap[user.id] || '-'}</td>
                          <td className="p-2">{formatDate(user.created_at)}</td>
                        </>
                      ) : (
                        <>
                          <td className="p-2">{userOrg ? userOrg.name : '-'}</td>
                          <td className="p-2">
                            {userTeam ? userTeam.name : '-'}
                          </td>
                          <td className="p-2">{formatDate(user.created_at)}</td>
                        </>
                      )
                    )}
                    <td className="p-2 text-center hidden">TODO</td>
                    <td className="p-2 text-center">
                      -{/* TODO: enable when editing is supported:  */}
                      {/* <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => {
                            setUserToEdit(user);
                            setEditDialogOpen(true);
                          }}
                          className="text-blue-500 hover:text-blue-700"
                        >
                          <Edit size={14} />
                        </button>
                        <button
                          onClick={() => {
                            setUserToDelete(user.id);
                            setDeleteDialogOpen(true);
                          }}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X size={14} />
                        </button>
                      </div> */}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    );
  };

  // Render the search bar with filter dropdown
  const renderSearchBar = (placeholder, value, onChange, width = '30%') => {
    return (
      <div className="mb-4 relative">
        <div className="flex items-center gap-2">
          <div className={`relative w-[${width}]`}>
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              className="pl-10 w-full"
              placeholder={placeholder}
              value={value}
              onChange={(e) => onChange(e.target.value)}
            />
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="flex items-center gap-2">
                <Filter className="h-4 w-4" />
                <span>
                  {filters.filterType === 'all' && 'Alle Gebruikers'}
                  {filters.filterType === 'withOrganization' &&
                    'Gebruikers bij Organisaties'}
                  {filters.filterType === 'withoutOrganization' &&
                    'Particuliere Gebruikers'}
                </span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => updateFilterType('all')}>
                Alle Gebruikers
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => updateFilterType('withOrganization')}
              >
                Gebruikers bij Organisaties
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => updateFilterType('withoutOrganization')}
              >
                Particuliere Gebruikers
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    );
  };

  // Render the organization card
  const renderOrganizationCard = (org: OrganizationOverview) => {
    return (
      <div
        key={org.name}
        className="border rounded-lg overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
        onClick={() => viewSingleOrg(org)}
      >
        <div className="p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-md overflow-hidden bg-gray-100 flex items-center justify-center">
              <img
                src={getIdenticon(org)}
                alt={`${org.name} logo`}
                className="w-full h-full object-contain"
              />
            </div>
            <div>
              <h3 className="font-medium">{org.name}</h3>
              <div className="text-sm text-gray-500">
                {countUsers(org)} medewerkers
              </div>
            </div>
          </div>

          <div className="mt-3 pt-3 border-t">
            <div className="text-sm font-medium mb-2">{isPreviewMode ? 'Samen' : 'Teams / Locaties'}</div>
            <div className="flex flex-wrap gap-2">
              {org.teams.map((team) => (
                <div
                  key={team.id}
                  className="text-xs bg-gray-100 px-2 py-1 rounded-full"
                  onClick={(e) => {
                    e.stopPropagation();
                    // filterUsersByTeam(team.name, org.name);
                  }}
                >
                  {team.name}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render the organization detail view
  const renderOrganizationDetail = () => {
    return (
      <div className="border rounded-lg p-6 shadow-lg">
        <div className="flex justify-between items-start mb-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-md overflow-hidden bg-gray-100 flex items-center justify-center">
              <img
                src={getIdenticon(singleOrgView)}
                alt={`${singleOrgView.name} logo`}
                className="w-full h-full object-contain"
              />
            </div>
            <h3 className="text-2xl font-medium">{singleOrgView.name}</h3>
          </div>
          <div className="text-right flex gap-4">
            <div className="flex items-center gap-1">
              <Users className="h-5 w-5 text-gray-500" />
              <div className="font-medium">{countUsers(singleOrgView)}</div>
            </div>
            <div className="flex items-center gap-1">
              <MapPin className="h-5 w-5 text-gray-500" />
              <div className="font-medium">
                {Math.floor(countUsers(singleOrgView) / 25)}
              </div>
            </div>
          </div>
        </div>

        {/*  low opacity for fake data */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8 opacity-30">
          <div>
            <h4 className="text-lg font-medium mb-4">Contactpersonen</h4>
            <div className="space-y-4">
              {DUMMY_ORGANIZATIONS[0].contacts.map((contact) => (
                <div
                  key={contact.email}
                  className="flex items-center gap-3 p-3 border rounded-lg bg-gray-50"
                >
                  <img
                    src={contact.avatar}
                    alt={contact.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <div className="font-medium">{contact.name}</div>
                    <div className="text-sm text-gray-500 flex gap-2">
                      <a
                        href={`mailto:${contact.email}`}
                        className="text-blue-500 hover:underline"
                      >
                        {contact.email}
                      </a>
                      <span className="text-gray-400">|</span>
                      <a
                        href={`tel:${contact.phone || '+31 6 12345678'}`}
                        className="text-blue-500 hover:underline"
                      >
                        {contact.phone || '+31 6 12345678'}
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-lg font-medium mb-4">Organisatie Informatie</h4>
            <div className="grid grid-cols-2 gap-4 p-4 border rounded-lg bg-gray-50">
              <div>
                <div className="text-sm text-gray-500">Opgericht</div>
                <div>01/01/2020</div>
              </div>
              <div>
                <div className="text-sm text-gray-500">KVK nummer</div>
                <div>12345678</div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Adres</div>
                <div>Hoofdstraat 123</div>
                <div>1234 AB Amsterdam</div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Website</div>
                <div className="text-blue-500">
                  www.
                  {singleOrgView.name.toLowerCase().replace(/\s+/g, '')}
                  .nl
                </div>
              </div>
            </div>
          </div>
        </div>

        {singleOrgView && renderTeamsSection(singleOrgView)}
      </div>
    );
  };

  // Render the teams section in organization detail
  const renderTeamsSection = (org: OrganizationOverview) => {
    return (
      <div>
        {expandedTeam ? (
          <div className="flex items-center mb-6">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setExpandedTeam(null)}
                className="text-sm flex items-center gap-1 text-green-600 hover:text-green-700"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M19 12H5" />
                  <path d="M12 19l-7-7 7-7" />
                </svg>
                <span className="ml-1">Terug</span>
              </Button>
              <span className="font-medium text-lg">{isPreviewMode ? 'Samen' : 'Teams / Locaties'}</span>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-medium">{isPreviewMode ? 'Samen' : 'Teams / Locaties'}</h4>
          </div>
        )}

        {expandedTeam ? (
          <div className="space-y-4">
            {TeamDetailsCard(expandedTeam)}
            <h5 className="font-medium mt-4">Medewerkers</h5>
            TODO
            {/* {renderUsersTable(
              users.filter(
                (user) =>
                  (user.team === expandedTeam.name ||
                    user.team?.includes(expandedTeam.name)) &&
                  (user.organisatie === expandedTeam.organization ||
                    user.organisatie?.includes(expandedTeam.organization)),
              ),
            )} */}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {org.teams.map((team) => (
              <div
                key={team.id}
                className="border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => setExpandedTeam(team)}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="font-medium">{team.name}</div>
                  <div className="text-sm">{team.users.length} medewerkers</div>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <MapPin className="h-4 w-4" />
                  <span>{team.name}</span>
                </div>
                <div className="mt-3 pt-3 border-t flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full overflow-hidden">
                    {/* <img
                        src={team.managerAvatar}
                        alt={team.manager}
                        className="w-full h-full object-cover"
                      /> */}
                  </div>
                  <div className="text-xs">TODO: manager</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-start items-center mb-4">
        <div
          className="flex items-center gap-4"
          style={{ marginRight: '22px' }}
        >
          <h1 className="text-2xl font-semibold">{isPreviewMode ? 'HnH Gebruikers' : 'Gebruikers'}</h1>
        </div>
        <div className="flex gap-2">
          {isPreviewMode ? (
            <>
              <Button
                className="bg-green-600 hover:bg-green-700 text-sm"
                onClick={() => setHnhSamenDialogOpen(true)}
              >
                + Samen
              </Button>
              <Button
                className="bg-green-600 hover:bg-green-700 text-sm"
                onClick={() => setHnhUserDialogOpen(true)}
              >
                + HnH Gebruiker
              </Button>
            </>
          ) : (
            <>
              <div>{CreateForm('organisaties', activeTab, setActiveTab, false)}</div>
              <div>{CreateForm('teams', activeTab, setActiveTab, false)}</div>
              <div>{CreateForm('gebruikers', activeTab, setActiveTab, false)}</div>
            </>
          )}
        </div>
      </div>
      <div className="w-full">
        {/* Header section */}
        {teamFilterActive &&
        activeTeamFilter &&
        !singleOrgView &&
        expandedTeam ? (
          <div></div>
        ) : null}

        {/* Team filter header or organization view header */}
        {teamFilterActive &&
        activeTeamFilter &&
        !singleOrgView &&
        expandedTeam ? (
          <div></div>
        ) : singleOrgView ? (
          <div className="flex items-center mb-6">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={backToAllOrgs}
                className="text-sm flex items-center gap-1 text-green-600 hover:text-green-700"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M19 12H5" />
                  <path d="M12 19l-7-7 7-7" />
                </svg>
                <span className="ml-1">{isPreviewMode ? 'Terug naar Samen' : 'Terug naar Organisaties'}</span>
              </Button>
              <span className="font-medium text-lg">{singleOrgView.name}</span>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-6 mb-6">
            {!isPreviewMode && (
              <div
                className={`flex items-center gap-2 cursor-pointer ${activeTab === 'organisaties' ? 'text-green-600 font-medium' : 'text-gray-500 hover:text-gray-700'}`}
                onClick={() => setActiveTab('organisaties')}
              >
                <Building2
                  className={`h-5 w-5 ${activeTab === 'organisaties' ? 'text-green-600' : 'text-gray-500'}`}
                />
                <span>Organisaties</span>
              </div>
            )}
            <div
              className={`flex items-center gap-2 cursor-pointer ${activeTab === 'teams' ? 'text-green-600 font-medium' : 'text-gray-500 hover:text-gray-700'}`}
              onClick={() => setActiveTab('teams')}
            >
              <MapPin
                className={`h-5 w-5 ${activeTab === 'teams' ? 'text-green-600' : 'text-gray-500'}`}
              />
              <span>{isPreviewMode ? 'Samen' : 'Teams / Locaties'}</span>
            </div>
            <div
              className={`flex items-center gap-2 cursor-pointer ${activeTab === 'gebruikers' ? 'text-green-600 font-medium' : 'text-gray-500 hover:text-gray-700'}`}
              onClick={() => setActiveTab('gebruikers')}
            >
              <Users
                className={`h-5 w-5 ${activeTab === 'gebruikers' ? 'text-green-600' : 'text-gray-500'}`}
              />
              <span>{isPreviewMode ? 'HnH Gebruikers' : 'Gebruikers'}</span>
            </div>
          </div>
        )}

        {/* Users tab content */}
        {activeTab === 'gebruikers' && (
          <div className="mt-6">
            {teamFilterActive &&
            activeTeamFilter &&
            !singleOrgView &&
            expandedTeam ? (
              <div>
                {renderTeamFilterHeader()}
                {TeamDetailsCard(expandedTeam)}
                <h5 className="font-medium">Medewerkers</h5>
                {renderSearchBar(
                  'Zoek medewerkers op naam, email...',
                  filters.searchTerm,
                  updateSearchTerm,
                )}
                {renderUsersTable(filteredUsers)}
              </div>
            ) : (
              <>
                {teamFilterActive && activeTeamFilter && !singleOrgView && (
                  <div className="mb-4 flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          clearTeamFilter();
                          // Find the organization and view it
                          const org = organizations.find(
                            (o) => o.name === activeTeamFilter.organization,
                          );
                          if (org) {
                            setActiveTab('organisaties');
                            viewSingleOrg(org);
                          }
                        }}
                        className="text-sm flex items-center gap-1 text-green-600 hover:text-green-700"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M19 12H5" />
                          <path d="M12 19l-7-7 7-7" />
                        </svg>
                        Terug naar organisatie
                      </Button>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">
                        {activeTeamFilter.team}
                      </span>
                      <span className="text-gray-500">
                        ({activeTeamFilter.organization})
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={clearTeamFilter}
                        className="text-xs flex items-center gap-1 ml-2"
                      >
                        <X size={14} />
                        Filter wissen
                      </Button>
                    </div>
                  </div>
                )}
                {renderSearchBar(
                  'Zoek gebruikers op naam, email, organisatie...',
                  filters.searchTerm,
                  updateSearchTerm,
                )}
                {renderUsersTable(filteredUsers)}
              </>
            )}
          </div>
        )}

        {/* Organizations tab content */}
        {activeTab === 'organisaties' && !isPreviewMode && (
          <div className="mt-6">
            {singleOrgView ? (
              <div>{renderOrganizationDetail()}</div>
            ) : (
              <div>
                {renderSearchBar(
                  'Zoek organisaties...',
                  orgSearchTerm,
                  setOrgSearchTerm,
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredOrganizations.map((org) =>
                    renderOrganizationCard(org),
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Teams tab content */}
        {activeTab === 'teams' && (
          <div className="mt-6">
            {renderSearchBar(
              isPreviewMode ? 'Zoek Samen groepen...' : 'Zoek teams op naam, locatie...',
              teamSearchTerm,
              setTeamSearchTerm,
            )}
            {isPreviewMode ? SamenTable(samenGroups) : TeamsTable(
              organizations,
              teamIdToOrg,
              setTeamToEdit,
              setEditDialogOpen,
              setTeamToDelete,
              setTeamDeleteDialogOpen,
            )}
          </div>
        )}
      </div>

      {/* Delete User Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Gebruiker verwijderen</AlertDialogTitle>
            <AlertDialogDescription>
              Weet u zeker dat u deze gebruiker wilt verwijderen? Deze actie kan
              niet ongedaan worden gemaakt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (userToDelete) {
                  deleteUser(userToDelete);
                  setUserToDelete(null);
                }
              }}
              className="bg-red-600 hover:bg-red-700"
            >
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Team Confirmation Dialog */}
      <AlertDialog
        open={teamDeleteDialogOpen}
        onOpenChange={setTeamDeleteDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Team verwijderen</AlertDialogTitle>
            <AlertDialogDescription>
              Weet u zeker dat u dit team wilt verwijderen? Deze actie kan niet
              ongedaan worden gemaakt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (teamToDelete) {
                  deleteTeam(teamToDelete);
                  setTeamToDelete(null);
                }
              }}
              className="bg-red-600 hover:bg-red-700"
            >
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            {/* TODO: don't combine these 3 use cases wtf */}
            <DialogTitle>
              {userToEdit
                ? 'Gebruiker bewerken'
                : teamToEdit
                  ? 'Team bewerken'
                  : 'Organisatie bewerken'}
            </DialogTitle>
          </DialogHeader>

          {userToEdit && (
            <form className="space-y-4">
              <div className="flex items-center justify-center mb-4">
                <div className="relative">
                  <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden border-2 border-gray-300">
                    {userToEdit.avatar_url ? (
                      <img
                        src={userToEdit.avatar_url}
                        alt={userToEdit.naam || 'User'}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <img
                        src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${userToEdit.naam || userToEdit.email || userToEdit.id}`}
                        alt={userToEdit.naam || 'User'}
                        className="w-full h-full object-cover"
                      />
                    )}
                  </div>
                  <label
                    htmlFor="edit-user-image"
                    className="absolute bottom-0 right-0 bg-green-600 text-white p-1 rounded-full cursor-pointer hover:bg-green-700 transition-colors"
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M18.5 2.50001C18.8978 2.10219 19.4374 1.87869 20 1.87869C20.5626 1.87869 21.1022 2.10219 21.5 2.50001C21.8978 2.89784 22.1213 3.4374 22.1213 4.00001C22.1213 4.56262 21.8978 5.10219 21.5 5.50001L12 15L8 16L9 12L18.5 2.50001Z"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <input
                      id="edit-user-image"
                      type="file"
                      accept="image/*"
                      className="hidden"
                    />
                  </label>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-name">Naam</Label>
                <Input id="edit-name" defaultValue={userToEdit.naam || ''} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-email">Email</Label>
                <Input
                  id="edit-email"
                  type="email"
                  defaultValue={userToEdit.email || ''}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-phone">Telefoonnummer</Label>
                <Input
                  id="edit-phone"
                  type="tel"
                  defaultValue={userToEdit.telefoonnummer || ''}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-org">Organisatie</Label>
                <Input
                  id="edit-org"
                  defaultValue={userToEdit.organisatie || ''}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-team">Team / Locatie</Label>
                <Input id="edit-team" defaultValue={userToEdit.team || ''} />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button
                  variant="outline"
                  type="button"
                  onClick={() => setEditDialogOpen(false)}
                >
                  Annuleren
                </Button>
                <Button
                  type="button"
                  className="bg-green-600 hover:bg-green-700"
                  onClick={() => {
                    // Update user in state
                    const updatedUser = {
                      ...userToEdit,
                      naam: (
                        document.getElementById('edit-name') as HTMLInputElement
                      )?.value,
                      email: (
                        document.getElementById(
                          'edit-email',
                        ) as HTMLInputElement
                      )?.value,
                      telefoonnummer: (
                        document.getElementById(
                          'edit-phone',
                        ) as HTMLInputElement
                      )?.value,
                      organisatie: (
                        document.getElementById('edit-org') as HTMLInputElement
                      )?.value,
                      team: (
                        document.getElementById('edit-team') as HTMLInputElement
                      )?.value,
                    };

                    // Note: Since users are now derived from organizations,
                    // we can't directly update them here. The organizations
                    // would need to be refreshed to see changes.
                    // For now, just update the filtered users for immediate UI feedback
                    setFilteredUsers(
                      filteredUsers.map((u) =>
                        u.id === updatedUser.id ? updatedUser : u,
                      ),
                    );

                    // Close dialog
                    setEditDialogOpen(false);
                    setUserToEdit(null);
                  }}
                >
                  Opslaan
                </Button>
              </div>
            </form>
          )}
          {teamToEdit && (
            <form className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-team-name">Naam</Label>
                <Input
                  id="edit-team-name"
                  defaultValue={teamToEdit.name || ''}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-team-org">Organisatie</Label>
                <Input
                  id="edit-team-org"
                  defaultValue={teamToEdit.organization || ''}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-team-location">Locatie</Label>
                <Input
                  id="edit-team-location"
                  defaultValue={teamToEdit.location || ''}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-team-manager">Manager</Label>
                <Input
                  id="edit-team-manager"
                  defaultValue={teamToEdit.manager || ''}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-team-email">Email</Label>
                <Input
                  id="edit-team-email"
                  type="email"
                  defaultValue={teamToEdit.managerEmail || ''}
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button
                  variant="outline"
                  type="button"
                  onClick={() => setEditDialogOpen(false)}
                >
                  Annuleren
                </Button>
                <Button
                  type="button"
                  className="bg-green-600 hover:bg-green-700"
                  onClick={() => {
                    // Update team in state
                    const updatedTeam = {
                      ...teamToEdit,
                      name: (
                        document.getElementById(
                          'edit-team-name',
                        ) as HTMLInputElement
                      )?.value,
                      organization: (
                        document.getElementById(
                          'edit-team-org',
                        ) as HTMLInputElement
                      )?.value,
                      location: (
                        document.getElementById(
                          'edit-team-location',
                        ) as HTMLInputElement
                      )?.value,
                      manager: (
                        document.getElementById(
                          'edit-team-manager',
                        ) as HTMLInputElement
                      )?.value,
                      managerEmail: (
                        document.getElementById(
                          'edit-team-email',
                        ) as HTMLInputElement
                      )?.value,
                    };

                    // Update in state
                    // TODO: update in DB
                    // setTeams(
                    //   teams.map((t) =>
                    //     t.id === updatedTeam.id ? updatedTeam : t,
                    //   ),
                    // );
                    // setFilteredTeams(
                    //   filteredTeams.map((t) =>
                    //     t.id === updatedTeam.id ? updatedTeam : t,
                    //   ),
                    // );

                    // Close dialog
                    setEditDialogOpen(false);
                    setTeamToEdit(null);
                  }}
                >
                  Opslaan
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* HnH: Create Samen Group Dialog */}
      <Dialog open={hnhSamenDialogOpen} onOpenChange={setHnhSamenDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Samen Groep Toevoegen</DialogTitle>
          </DialogHeader>
          <form
            className="space-y-4"
            onSubmit={async (e) => {
              e.preventDefault();
              setHnhSaving(true);
              try {
                const formData = new FormData(e.currentTarget);
                const name = formData.get('samenName') as string;

                const { error } = await supabase
                  .from('samen')
                  .insert({ name });

                if (error) throw error;

                toast({ title: 'Samen groep aangemaakt', description: `"${name}" is aangemaakt.` });
                setHnhSamenDialogOpen(false);
                window.location.reload();
              } catch (error: any) {
                console.error('Error creating Samen group:', error);
                toast({ title: 'Fout', description: error?.message || 'Kon Samen groep niet aanmaken.', variant: 'destructive' });
              } finally {
                setHnhSaving(false);
              }
            }}
          >
            <div className="space-y-2">
              <Label htmlFor="samenName">Naam van de Samen groep</Label>
              <Input id="samenName" name="samenName" placeholder="Bijv. Familie Jansen" required />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" type="button" onClick={() => setHnhSamenDialogOpen(false)}>
                Annuleren
              </Button>
              <Button type="submit" className="bg-green-600 hover:bg-green-700" disabled={hnhSaving}>
                {hnhSaving ? 'Aanmaken...' : 'Aanmaken'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* HnH: Create HnH Gebruiker Dialog */}
      <Dialog open={hnhUserDialogOpen} onOpenChange={setHnhUserDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>HnH Gebruiker Toevoegen</DialogTitle>
          </DialogHeader>
          <form
            className="space-y-4"
            onSubmit={async (e) => {
              e.preventDefault();
              setHnhSaving(true);
              try {
                // Create the invite (team_id used for backwards compat, samen link done separately)
                const { data: invite, error } = await supabase
                  .from('user_invite')
                  .insert({
                    invite_code: hnhInviteCode,
                    role: 'hoofdhartuser' as any,
                    team_id: null,
                  })
                  .select()
                  .single();

                // Note: samen_user_link will be created when user registers
                // Store the samen_id in a comment or handle in registration flow

                if (error) throw error;

                toast({ title: 'Uitnodigingscode aangemaakt', description: `Code "${hnhInviteCode}" is klaar voor gebruik.` });
                setHnhUserDialogOpen(false);
                setHnhInviteCode('');
                setHnhSelectedTeamId('');
              } catch (error: any) {
                console.error('Error creating invite:', error);
                toast({ title: 'Fout', description: error?.message || 'Kon uitnodiging niet aanmaken.', variant: 'destructive' });
              } finally {
                setHnhSaving(false);
              }
            }}
          >
            <div className="space-y-2">
              <Label htmlFor="inviteCode">Uitnodigingscode</Label>
              <Input id="inviteCode" value={hnhInviteCode} onChange={(e) => setHnhInviteCode(e.target.value)} placeholder="Bijv. familie-jansen-2024" required />
              <p className="text-xs text-muted-foreground">De gebruiker vult deze code in bij registratie.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="samenGroup">Samen groep (optioneel)</Label>
              <Select value={hnhSelectedTeamId} onValueChange={setHnhSelectedTeamId}>
                <SelectTrigger>
                  <SelectValue placeholder="Kies een Samen groep..." />
                </SelectTrigger>
                <SelectContent>
                  {samenGroups.map((group) => (
                    <SelectItem key={group.id} value={group.id}>
                      {group.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">De gebruiker wordt automatisch aan deze groep gekoppeld.</p>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" type="button" onClick={() => setHnhUserDialogOpen(false)}>
                Annuleren
              </Button>
              <Button type="submit" className="bg-green-600 hover:bg-green-700" disabled={hnhSaving}>
                {hnhSaving ? 'Aanmaken...' : 'Aanmaken'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Render the teams table
// TODO: support filtering teams again?
const TeamsTable = (
  organizations: OrganizationOverview[],
  teamIdToOrg: Record<string, OrganizationOverview>,
  // organization: Organization,
  setTeamToEdit: (team: Team) => void,
  setEditDialogOpen: (open: boolean) => void,
  setTeamToDelete: (teamId: string) => void,
  setTeamDeleteDialogOpen: (open: boolean) => void,
) => {
  const allTeams = organizations.flatMap((org) => org.teams);
  const filteredTeams = allTeams; // for now

  return (
    <div className="border rounded-md overflow-hidden">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="bg-gray-50 border-b">
            <th className="text-left p-2 pl-3">Naam</th>
            <th className="text-left p-2">Organisatie</th>
            {/* <th className="text-left p-2">Locatie</th> */}
            {/* <th className="text-left p-2">Manager</th> */}
            {/* <th className="text-left p-2">Contact</th> */}
            <th className="text-left p-2">Medewerkers</th>
            <th className="text-left p-2 w-10">Acties</th>
          </tr>
        </thead>

        <tbody>
          {filteredTeams.length === 0 ? (
            <tr>
              <td colSpan={7} className="p-2 text-center">
                Geen teams gevonden
              </td>
            </tr>
          ) : (
            filteredTeams.map((team) => (
              <tr
                key={team.id}
                className="border-b hover:bg-gray-50 cursor-pointer"
                // onClick={() =>
                //   filterUsersByTeam(team.name, team.organization)
                // }
              >
                <td className="p-2 pl-3 font-medium">{team.name}</td>
                <td className="p-2">{teamIdToOrg[team.id].name}</td>
                {/* <td className="p-2">{team.location}</td> */}

                {/* <td className="p-2">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full overflow-hidden">
                      <img
                        src={team.managerAvatar}
                        alt={team.manager}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <span></span>
                  </div>
                </td> */}

                {/* <td className="p-2">
                  <a
                    href={`mailto:${team.managerEmail}`}
                    className="text-blue-500 hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {team.managerEmail}
                  </a>
                </td> */}

                <td className="p-2">{team.users.length}</td>
                <td className="p-2 text-center">
                  -{/* TODO: enable when editing is supported:  */}
                  {/* <div className="flex items-center justify-center gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setTeamToEdit(team);
                        setEditDialogOpen(true);
                      }}
                      className="text-blue-500 hover:text-blue-700"
                    >
                      <Edit size={14} />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setTeamToDelete(team.id);
                        setTeamDeleteDialogOpen(true);
                      }}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X size={14} />
                    </button>
                  </div> */}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

const SamenTable = (
  groups: { id: string; name: string; member_count: number }[],
) => {
  return (
    <div className="border rounded-md overflow-hidden">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="bg-gray-50 border-b">
            <th className="text-left p-2 pl-3">Naam</th>
            <th className="text-left p-2">Leden</th>
          </tr>
        </thead>
        <tbody>
          {groups.length === 0 ? (
            <tr>
              <td colSpan={2} className="p-2 text-center">
                Geen Samen groepen gevonden
              </td>
            </tr>
          ) : (
            groups.map((group) => (
              <tr
                key={group.id}
                className="border-b hover:bg-gray-50"
              >
                <td className="p-2 pl-3 font-medium">{group.name}</td>
                <td className="p-2">{group.member_count}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

// Render the team details card
const TeamDetailsCard = (expandedTeam: DummyTeam | null) => {
  return (
    <div className="bg-gray-50 p-4 rounded-lg border space-y-2 mb-6">
      <div className="flex items-center justify-between">
        <div className="font-medium">{expandedTeam?.name}</div>
        <div className="text-sm">{expandedTeam?.userCount} medewerkers</div>
      </div>
      <div className="grid grid-cols-2 gap-2 text-sm border-t pt-2 mt-2">
        <div>
          <span className="text-gray-500">Contactpersoon:</span>
          <div>{expandedTeam?.manager}</div>
        </div>
        <div>
          <span className="text-gray-500">Email:</span>
          <div>
            <a
              href={`mailto:${expandedTeam?.managerEmail}`}
              className="text-blue-500 hover:underline"
            >
              {expandedTeam?.managerEmail}
            </a>
          </div>
        </div>
        <div>
          <span className="text-gray-500">Telefoon:</span>
          <div>
            <a
              href="tel:+31612345678"
              className="text-blue-500 hover:underline"
            >
              +31 6 12345678
            </a>
          </div>
        </div>
        <div>
          <span className="text-gray-500">Adres:</span>
          <div>Hoofdstraat 123, 1234 AB {expandedTeam?.location}</div>
        </div>
      </div>
    </div>
  );
};

/**
 * Render the add user/organization/team form
 */
const CreateForm = (
  formType: 'gebruikers' | 'organisaties' | 'teams',
  activeTab: 'gebruikers' | 'organisaties' | 'teams',
  setActiveTab: (tab: 'gebruikers' | 'organisaties' | 'teams') => void,
  isHnhMode: boolean = false,
) => {
  const getLabel = () => {
    if (isHnhMode) {
      return formType === 'teams' ? 'Samen' : 'HnH Gebruiker';
    }
    return formType === 'organisaties' ? 'Organisatie' : formType === 'teams' ? 'Team' : 'Gebruiker';
  };

  const title = `${getLabel()} Toevoegen`;

  return (
    <Dialog>
      <DialogTrigger asChild>
        {/* org, team, and user creation not yet supported in UI */}
        <Button
          disabled={true}
          variant="default"
          className="bg-green-600 hover:bg-green-700 text-sm"
          onClick={() => {
            if (activeTab !== formType) {
              setActiveTab(formType);
            }
          }}
        >
          + {getLabel()}
        </Button>
      </DialogTrigger>

      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        {activeTab === 'gebruikers' ? (
          <form className="space-y-4">
            <div className="flex items-center justify-center mb-4">
              <div className="relative">
                <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden border-2 border-gray-300">
                  <svg
                    width="40"
                    height="40"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21"
                      stroke="#9CA3AF"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z"
                      stroke="#9CA3AF"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <label
                  htmlFor="user-image-upload"
                  className="absolute bottom-0 right-0 bg-green-600 text-white p-1 rounded-full cursor-pointer hover:bg-green-700 transition-colors"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 5V19M5 12H19"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <input
                    id="user-image-upload"
                    type="file"
                    accept="image/*"
                    className="hidden"
                  />
                </label>
              </div>
            </div>
            <div className="space-y-2">
              <Input placeholder="Name" />
            </div>
            <div className="space-y-2">
              <Input type="email" placeholder="Email" />
            </div>
            <div className="space-y-2">
              <Input type="tel" placeholder="Telefoonnummer" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="organisatie">Organisatie / Locatie</Label>
              <Input id="organisatie" placeholder="Organisatie / Locatie" />
              <div className="flex items-center gap-2 mt-1">
                <Switch
                  id="no-org"
                  onCheckedChange={(checked) => {
                    // Handle the team input field
                    const teamInput = document.getElementById(
                      'team',
                    ) as HTMLInputElement;
                    const noTeamSwitch = document.getElementById(
                      'no-team',
                    ) as HTMLInputElement;
                    if (teamInput && noTeamSwitch) {
                      if (checked) {
                        // Disable team input and switch when no org is selected
                        teamInput.disabled = true;
                        noTeamSwitch.disabled = true;
                      } else {
                        // Enable team input and switch when org is selected
                        teamInput.disabled = false;
                        noTeamSwitch.disabled = false;
                      }
                    }
                  }}
                />
                <Label htmlFor="no-org">Geen organisatie</Label>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="team">Team / Locatie</Label>
              <Input
                id="team"
                placeholder="Team"
                disabled={
                  document.getElementById('no-org')?.ariaChecked === 'true'
                }
              />
              <div className="flex items-center gap-2 mt-1">
                <Switch id="no-team" disabled={false} />
                <Label htmlFor="no-team">Geen team</Label>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Switch id="magic-link" />
                <Label htmlFor="magic-link">Stuur magic link</Label>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" type="button">
                Annuleren
              </Button>
              <Button className="bg-green-600 hover:bg-green-700">
                Opslaan
              </Button>
            </div>
          </form>
        ) : (
          // org (and team) creation form (TODO: don't combine these)
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              // Handle form submission
              const formData = new FormData(e.currentTarget);

              // Get the logo URL from the data attribute
              const imagePreview = document.querySelector('#org-image-preview');
              const logoUrl =
                (imagePreview as HTMLDivElement)?.dataset.logoUrl ||
                'https://images.unsplash.com/photo-1572021335469-31706a17aaef?w=100&q=80';

              const newOrg = {
                name: formData.get('orgName') as string,
                employees: parseInt(formData.get('employees') as string) || 0,
                logo: logoUrl,
                contacts: [
                  {
                    name: formData.get('contactName') as string,
                    email: formData.get('contactEmail') as string,
                    phone:
                      (formData.get('contactPhone') as string) ||
                      '+31 6 12345678',
                    avatar: `https://api.dicebear.com/7.x/avataaars/svg?seed=${formData.get('contactName')}`,
                  },
                ],
              };

              // TODO: actually create org

              // Close dialog
              const closeButton = document.querySelector(
                'button[type="button"]',
              );
              (closeButton as HTMLButtonElement).click();
            }}
          >
            <div className="flex items-center justify-center mb-4">
              <div className="relative">
                <div
                  id="org-image-preview"
                  className="w-24 h-24 rounded-md bg-gray-200 flex items-center justify-center overflow-hidden border-2 border-gray-300"
                >
                  <svg
                    width="40"
                    height="40"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M3 21H21M3 18H21M8 18V13M16 18V13M10 13V10M14 13V10M12 13V7M7 3H17C17.5304 3 18.0391 3.21071 18.4142 3.58579C18.7893 3.96086 19 4.46957 19 5V18H5V5C5 4.46957 5.21071 3.96086 5.58579 3.58579C5.96086 3.21071 6.46957 3 7 3Z"
                      stroke="#9CA3AF"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <label
                  htmlFor="org-image-upload"
                  className="absolute bottom-0 right-0 bg-green-600 text-white p-1 rounded-full cursor-pointer hover:bg-green-700 transition-colors"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 5V19M5 12H19"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <input
                    id="org-image-upload"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;

                      try {
                        // Get form data for organization name
                        const form = e.target.closest('form');
                        const orgNameInput = form?.querySelector(
                          'input[placeholder="Organisatie naam"]',
                        ) as HTMLInputElement;
                        const orgName =
                          orgNameInput?.value || `new-org-${Date.now()}`;

                        // Upload image to Supabase storage
                        const fileExt = file.name.split('.').pop();
                        const fileName = `org-${orgName.replace(/\s+/g, '-').toLowerCase()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
                        const filePath = `organizations/${fileName}`;

                        const { error: uploadError } = await supabase.storage
                          .from('organizations')
                          .upload(filePath, file);

                        if (uploadError) throw uploadError;

                        // Get public URL
                        const {
                          data: { publicUrl },
                        } = supabase.storage
                          .from('organizations')
                          .getPublicUrl(filePath);

                        // Update the image preview
                        const imagePreview = e.target
                          .closest('.relative')
                          ?.querySelector('div');
                        if (imagePreview) {
                          // Clear existing content
                          imagePreview.innerHTML = '';

                          // Add the image
                          const img = document.createElement('img');
                          img.src = publicUrl;
                          img.alt = orgName;
                          img.className = 'w-full h-full object-contain';
                          imagePreview.appendChild(img);

                          // Store the URL in a data attribute for form submission
                          imagePreview.dataset.logoUrl = publicUrl;
                        }
                      } catch (error) {
                        console.error(
                          'Error uploading organization image:',
                          error,
                        );
                      }
                    }}
                  />
                </label>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Organisatie</Label>
              <Input name="orgName" placeholder="Organisatie naam" required />
            </div>

            <div className="space-y-2">
              <Label>Team / Locatie</Label>
              <Input name="teamName" placeholder="Team / Locatie" />
            </div>

            <div className="space-y-2">
              <Label>Contactpersoon</Label>
              <Input name="contactName" placeholder="Naam" required />
            </div>

            <div className="space-y-2">
              <Input name="contactRole" placeholder="Functie" />
            </div>

            <div className="space-y-2">
              <Input
                name="contactEmail"
                type="email"
                placeholder="Email"
                required
              />
            </div>

            <div className="space-y-2">
              <Input
                name="employees"
                type="number"
                placeholder="Aantal medewerkers"
                required
              />
            </div>

            <div className="space-y-2">
              <Textarea name="notes" placeholder="Notes" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contactPhone">Telefoonnummer</Label>
              <Input
                id="contactPhone"
                name="contactPhone"
                type="tel"
                placeholder="+31 6 12345678"
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button
                variant="outline"
                type="button"
                onClick={() => {
                  const closeButton = document.querySelector(
                    '[data-radix-collection-item]',
                  );
                  if (closeButton instanceof HTMLElement) closeButton.click();
                }}
              >
                Annuleren
              </Button>
              <Button type="submit" className="bg-green-600 hover:bg-green-700">
                Opslaan
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

// TODO: make 1 unit test for this :)
/**
 * Extract list of all unique profiles from
 * TODO: need to also show users belonging to NO team (and thus no org)
 */
